import { Anchor, Button, Div, Flex, FormStatus, H2, IconPrimary, P } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { refresh as Refresh } from '@dnb/eufemia/icons';
import type { TeamWithAppDetailsDto } from '@portals/shared/portal/TeamDto';
import { type JSX, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { useUser } from '@/hooks/useUser';

import type { AddMcpFormData } from './schema';

type SelectAppStepProps = {
  formId: string;
};

export default function SelectAppStep({ formId }: SelectAppStepProps): JSX.Element {
  const { user } = useUser();

  const teams = useMemo(() => user?.teams ?? [], [user?.teams]);

  const { data, update } = Form.useData<AddMcpFormData>(formId);
  const selectedTeamId = data?.teamId;

  const {
    data: team,
    isValidating: teamLoading,
    mutate: mutateTeam,
  } = useSWR<TeamWithAppDetailsDto>(selectedTeamId ? `/team/${selectedTeamId}` : null);

  const providerApps = useMemo(
    () =>
      (team?.apps ?? [])
        .filter(({ isProviderApp }) => isProviderApp)
        .sort((a, b) => a.app.name.localeCompare(b.app.name)),
    [team?.apps],
  );

  const noProviderApps = Boolean(selectedTeamId) && !teamLoading && providerApps.length === 0;

  return (
    <Flex.Stack>
      <div>
        <H2 bottom="small" top="0">
          Select app
        </H2>
        <P>Select the team and provider app that will own the new MCP.</P>
      </div>

      <Field.Selection
        label="Team"
        onChange={() => {
          update('/appId', undefined);
        }}
        path="/teamId"
        required
        size="large"
        variant="autocomplete"
        width="stretch"
      >
        {teams.map((team) => (
          <Field.Option key={team.id} title={team.name} value={team.id} />
        ))}
      </Field.Selection>

      <Div className="flex gap-4 items-end">
        <Field.Selection
          className="flex-1"
          disabled={!selectedTeamId || teamLoading || noProviderApps}
          label="App"
          path="/appId"
          required
          size="large"
          variant="autocomplete"
          width="stretch"
        >
          {providerApps.map(({ app }) => (
            <Field.Option key={app.id} title={app.name} value={app.id} />
          ))}
        </Field.Selection>
        <Button
          aria-label="Refresh app list"
          disabled={!selectedTeamId || teamLoading}
          icon={<IconPrimary icon={Refresh} />}
          onClick={() => mutateTeam()}
          size="large"
          variant="secondary"
        />
      </Div>

      {noProviderApps && (
        <FormStatus state="information" stretch>
          This team has no provider app yet.{' '}
          <Anchor
            element={RouterLink}
            rel="noopener noreferrer"
            target="_blank"
            to={`/team/${selectedTeamId}/application/new?isProviderApp=true`}
          >
            Create a provider app
          </Anchor>{' '}
          first, then come back to add your MCP.
        </FormStatus>
      )}

      {teams.length === 0 && <P>You are not a member of any team. Join or create a team to publish an MCP.</P>}
    </Flex.Stack>
  );
}
